/**
 * 资源节点 Excel — 客户端模板下载 + 导出。
 *
 * 列与后端 backend/src/services/resourceNodeImportMaps.ts 的 IMPORT_FIELD_BY_HEADER
 * 一一对应(导出写中文标签、导入读中文标签),改动须两边同步。
 * 创建-only / 仅结构：不含排产资源列；可排产节点保存后由系统自动生成排产资源。
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import type { ResourceNode, ResourceNodeClass } from '../ProcessTemplateV2/types';
import type { ResourceNodeEquipmentCatalog } from '../../types/resourceNodeEquipmentCatalog';
import {
  NODE_CLASS_LABEL,
  NODE_SUBTYPE_OPTIONS,
  flattenNodes,
} from './resourceNodeConstants';

const SHEET_NAME = '节点';
const INSTRUCTION_SHEET_NAME = '填写说明';
const EXAMPLE_SHEET_NAME = '填写示例';
const DICTIONARY_SHEET_NAME = '模板字典';
export const MAX_RESOURCE_NODE_IMPORT_ROWS = 1_000;
export const MAX_RESOURCE_NODE_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

/** 导入/导出列(带 `*` 表示必填),顺序即列序。 */
export const RESOURCE_NODE_EXCEL_HEADERS = [
  '节点编码*',
  '节点名称*',
  '节点类型*',
  '子类型',
  '上级节点编码',
  '归属范围*',
  '部门编码',
  '设备形态',
  '设备类型',
  '设备型号',
  '排序',
  '启用',
];

const EXCEL_COLORS = {
  requiredFill: 'FFF4CCCC',
  requiredFont: 'FF9C0006',
  conditionalFill: 'FFFFF2CC',
  conditionalFont: 'FF7F6000',
  optionalFill: 'FFD9EAF7',
  optionalFont: 'FF1F4E78',
  referenceFill: 'FFE7E6E6',
  referenceFont: 'FF595959',
  headerFill: 'FF1F4E78',
  headerFont: 'FFFFFFFF',
  successFill: 'FFE2F0D9',
  successFont: 'FF375623',
  errorFill: 'FFC00000',
  errorFont: 'FFFFFFFF',
  border: 'FFD9E2F3',
} as const;

/** 子类型枚举值 → 中文标签(全类合并;CIP/SIP 自身即标签) */
const SUBTYPE_LABEL: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  (Object.keys(NODE_SUBTYPE_OPTIONS) as ResourceNodeClass[]).forEach((cls) => {
    NODE_SUBTYPE_OPTIONS[cls].forEach((opt) => {
      map[opt.value] = opt.label;
    });
  });
  return map;
})();

const SCOPE_LABEL: Record<string, string> = { GLOBAL: '全局', DEPARTMENT: '部门' };
const SYSTEM_TYPE_LABEL: Record<string, string> = { SUS: '一次性', SS: '不锈钢', VIRTUAL: '虚拟' };

const exampleRows: Array<Array<string | null>> = [
  ['RN-GLB-GLOBAL-SIT-0001', '示例厂区', '厂区', null, null, '全局', null, null, null, null, '1', '是'],
  ['RN-GLB-GLOBAL-LIN-0001', '示例产线', '产线', null, 'RN-GLB-GLOBAL-SIT-0001', '全局', null, null, null, null, '1', '是'],
  ['RN-DPT-USP-ROM-0001', 'USP 主工艺房间', '房间', '主工艺房间', 'RN-GLB-GLOBAL-LIN-0001', '部门', 'USP', null, null, null, '1', '是'],
  ['RN-DPT-USP-EUN-0001', '示例虚拟设备', '设备', null, 'RN-DPT-USP-ROM-0001', '部门', 'USP', '虚拟', null, null, '1', '是'],
];

const HEADER_NOTES = [
  '必填。1—64 位，只允许英文字母、数字、连字符和下划线；文件内不能重复。',
  '必填。最长 120 个字符。',
  '必填。从下拉列表选择。',
  '条件必填：房间选择主工艺房间/辅助间/通用房间；工作站选择 CIP/SIP；组件可留空。',
  '厂区留空；其他节点必须填写上级节点编码。可引用数据库已有编码或本文件中的编码。',
  '必填。选择全局或部门。',
  '归属范围为部门时必填；全局时必须留空。',
  '设备节点必填；统一选择一次性、不锈钢或虚拟。非设备节点必须留空。',
  '物理设备必填；虚拟设备留空。只可选择设备字典中已启用的类型。',
  '物理设备必填；虚拟设备留空。请先选择设备类型，再选择对应型号。',
  '可选。只能填写 0—999999 的整数。',
  '可选。选择是或否；留空默认启用。',
] as const;

type WorksheetDataValidation = {
  type: 'list' | 'textLength' | 'whole' | 'custom';
  operator?: 'between';
  allowBlank: boolean;
  formulae: Array<string | number>;
  showErrorMessage?: boolean;
  errorStyle?: 'stop';
  errorTitle?: string;
  error?: string;
  showInputMessage?: boolean;
  promptTitle?: string;
  prompt?: string;
};

type WorksheetWithDataValidations = {
  dataValidations: {
    add: (range: string, validation: WorksheetDataValidation) => void;
  };
};

const addDataValidation = (
  worksheet: ExcelJS.Worksheet,
  range: string,
  validation: WorksheetDataValidation,
): void => {
  (worksheet as unknown as WorksheetWithDataValidations).dataValidations.add(range, validation);
};

const addListValidation = (
  worksheet: ExcelJS.Worksheet,
  range: string,
  formula: string,
  prompt: string,
  allowBlank = true,
): void => {
  addDataValidation(worksheet, range, {
    type: 'list',
    allowBlank,
    formulae: [formula],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: '请选择有效值',
    error: '请从下拉列表中选择，不要手工输入其他内容。',
    showInputMessage: true,
    promptTitle: '填写提示',
    prompt,
  });
};

const configureSheetHeader = (
  worksheet: ExcelJS.Worksheet,
  rowNumber = 1,
): void => {
  const header = worksheet.getRow(rowNumber);
  header.font = { bold: true, color: { argb: EXCEL_COLORS.headerFont } };
  header.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_COLORS.headerFill },
  };
  header.alignment = { vertical: 'middle', wrapText: true };
  header.height = 42;
  worksheet.autoFilter = {
    from: { row: rowNumber, column: 1 },
    to: { row: rowNumber, column: RESOURCE_NODE_EXCEL_HEADERS.length },
  };
};

const applyEntryColumnWidths = (worksheet: ExcelJS.Worksheet): void => {
  const widths = [27, 26, 14, 18, 28, 15, 15, 18, 20, 28, 12, 12];
  widths.forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });
  worksheet.getColumn(1).numFmt = '@';
  worksheet.getColumn(5).numFmt = '@';
};

const styleEntryCells = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  endRow: number,
): void => {
  const requiredColumns = new Set([1, 2, 3, 6]);
  const conditionalColumns = new Set([4, 5, 7, 8, 9, 10]);
  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
    for (let columnIndex = 1; columnIndex <= RESOURCE_NODE_EXCEL_HEADERS.length; columnIndex += 1) {
      const cell = worksheet.getCell(rowIndex, columnIndex);
      if (requiredColumns.has(columnIndex)) {
        cell.fill = fillFor(EXCEL_COLORS.requiredFill);
      } else if (conditionalColumns.has(columnIndex)) {
        cell.fill = fillFor(EXCEL_COLORS.conditionalFill);
      } else {
        cell.fill = fillFor(EXCEL_COLORS.optionalFill);
      }
      cell.border = {
        bottom: { style: 'hair', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'hair', color: { argb: EXCEL_COLORS.border } },
      };
    }
  }
};

const fillFor = (argb: string): ExcelJS.Fill => ({
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb },
});

const styleLegendCell = (
  cell: ExcelJS.Cell,
  fill: string,
  font: string,
): void => {
  cell.fill = fillFor(fill);
  cell.font = { bold: true, color: { argb: font } };
  cell.alignment = { vertical: 'middle', horizontal: 'center' };
};

const addErrorConditionalFormatting = (
  worksheet: ExcelJS.Worksheet,
  ref: string,
  formula: string,
  priority: number,
): void => {
  worksheet.addConditionalFormatting({
    ref,
    rules: [{
      type: 'expression',
      priority,
      formulae: [formula],
      style: {
        fill: fillFor(EXCEL_COLORS.errorFill),
        font: { color: { argb: EXCEL_COLORS.errorFont }, bold: true },
      },
    }],
  });
};

export const buildResourceNodeImportTemplate = (
  catalog: ResourceNodeEquipmentCatalog = {
    categories: [],
    models: [],
    capabilityDefinitions: [],
    stateProfiles: [],
  },
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'MFG8APS';
  workbook.subject = '资源节点批量导入';
  workbook.created = new Date();
  const enabledCategoryIds = new Set(
    catalog.categories.filter((category) => category.isActive).map((category) => category.id),
  );
  const activeModels = catalog.models
    .filter((model) => (
      model.isActive
      && model.selectableForNewNodes
      && enabledCategoryIds.has(model.categoryId)
    ))
    .sort((a, b) => (
      a.categoryName.localeCompare(b.categoryName)
      || a.sortOrder - b.sortOrder
      || a.modelName.localeCompare(b.modelName)
    ));
  const selectableCategoryIds = new Set(activeModels.map((model) => model.categoryId));
  const activeCategories = catalog.categories
    .filter((category) => category.isActive && selectableCategoryIds.has(category.id))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.categoryName.localeCompare(b.categoryName));

  const instructionSheet = workbook.addWorksheet(INSTRUCTION_SHEET_NAME);
  instructionSheet.addRows([
    ['资源节点批量导入'],
    ['按顺序完成：看示例 → 在“节点”Sheet 录入 → 上传预检 → 修正错误 → 确认导入'],
    [],
    ['三步完成', null],
    ['① 先看示例', '打开“填写示例”Sheet，了解厂区、产线、房间和设备应如何关联。'],
    ['② 正式录入', '只在“节点”Sheet 填写；不要改 Sheet 名、表头或添加列。'],
    ['③ 上传预检', '系统先校验再写入；任一新增行有错误时整批不会写入。'],
    [],
    ['关键规则', null],
    ['导入方式', '仅新增；节点编码已存在时自动跳过，不覆盖现有节点。'],
    ['文件限制', `只接受不超过 5 MB 的 .xlsx 文件；最多 ${MAX_RESOURCE_NODE_IMPORT_ROWS} 条数据；禁止公式。`],
    ['节点编码', '必填，1—64 位，只允许英文字母、数字、连字符和下划线；同一文件内不能重复。'],
    ['层级规则', '厂区无上级；产线属于厂区；房间属于产线；设备属于房间；上级可引用数据库已有节点或本文件中的节点编码。'],
    ['子类型', '房间选择主工艺房间/辅助间/通用房间；工作站选择 CIP/SIP；其他节点通常留空。'],
    ['设备字段', '设备形态统一在一个字段中选择一次性、不锈钢或虚拟。物理设备必须选择设备类型和型号；虚拟设备将类型和型号留空。'],
    ['归属范围', '选择“部门”时必须填写部门编码；选择“全局”时部门编码必须留空。'],
    ['排序/启用', '排序只能填 0—999999 的整数；启用仅选择“是/否”，留空默认启用。'],
    ['模板字典', activeModels.length > 0
      ? '模板仅带入人工维护且已启用的设备类型和型号；历史设备实例不会写入。字典调整后请重新下载模板。'
      : '当前没有可供新设备选择的型号；请先在系统“设备字典”新增可复用型号，再重新下载模板。'],
    [],
    ['颜色说明', '红色 = 必填；黄色 = 条件必填；蓝色 = 可选；灰色 = 字典参考/不可在 Excel 中维护。'],
    ['必填字段', '必须填写'],
    ['条件必填', '根据节点类型、归属范围或设备形态决定'],
    ['可选字段', '可以留空'],
    ['字典参考', '仅供查看，系统设备字典才是维护入口'],
  ]);
  instructionSheet.mergeCells('A1:B1');
  instructionSheet.mergeCells('A2:B2');
  instructionSheet.getColumn(1).width = 22;
  instructionSheet.getColumn(2).width = 94;
  instructionSheet.getCell('A1').font = {
    bold: true,
    size: 18,
    color: { argb: EXCEL_COLORS.headerFont },
  };
  instructionSheet.getCell('A1').fill = fillFor(EXCEL_COLORS.headerFill);
  instructionSheet.getCell('A1').alignment = { vertical: 'middle' };
  instructionSheet.getRow(1).height = 32;
  instructionSheet.getCell('A2').fill = fillFor('FFD9EAF7');
  instructionSheet.getCell('A2').font = { color: { argb: EXCEL_COLORS.optionalFont } };
  instructionSheet.getCell('A2').alignment = { vertical: 'middle', wrapText: true };
  instructionSheet.getRow(2).height = 30;
  instructionSheet.getCell('A5').value = {
    text: '① 先看示例',
    hyperlink: `#'${EXAMPLE_SHEET_NAME}'!A1`,
  };
  instructionSheet.getCell('A6').value = {
    text: '② 正式录入',
    hyperlink: `#'${SHEET_NAME}'!A1`,
  };
  instructionSheet.getCell('A18').value = {
    text: '模板字典',
    hyperlink: `#'${DICTIONARY_SHEET_NAME}'!A1`,
  };
  ['A5', 'A6', 'A18'].forEach((address) => {
    instructionSheet.getCell(address).font = {
      bold: true,
      underline: true,
      color: { argb: EXCEL_COLORS.optionalFont },
    };
  });
  ['A4', 'A9', 'A20'].forEach((address) => {
    instructionSheet.getCell(address).font = { bold: true, color: { argb: EXCEL_COLORS.headerFill } };
    instructionSheet.getCell(address).fill = fillFor('FFD9EAF7');
  });
  styleLegendCell(instructionSheet.getCell('A21'), EXCEL_COLORS.requiredFill, EXCEL_COLORS.requiredFont);
  styleLegendCell(instructionSheet.getCell('A22'), EXCEL_COLORS.conditionalFill, EXCEL_COLORS.conditionalFont);
  styleLegendCell(instructionSheet.getCell('A23'), EXCEL_COLORS.optionalFill, EXCEL_COLORS.optionalFont);
  styleLegendCell(instructionSheet.getCell('A24'), EXCEL_COLORS.referenceFill, EXCEL_COLORS.referenceFont);
  instructionSheet.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true };
  });
  instructionSheet.views = [{ state: 'frozen', ySplit: 2, showGridLines: false, zoomScale: 95 }];

  const nodeSheet = workbook.addWorksheet(SHEET_NAME);
  const exampleSheet = workbook.addWorksheet(EXAMPLE_SHEET_NAME);
  const dictionarySheet = workbook.addWorksheet(DICTIONARY_SHEET_NAME);

  const dictionaries: Array<[string, string[]]> = [
    ['节点类型', ['厂区', '产线', '房间', '设备', '组件', '工作站']],
    ['房间子类型', ['主工艺房间', '辅助间', '通用房间']],
    ['工作站子类型', ['CIP', 'SIP']],
    ['归属范围', ['全局', '部门']],
    ['部门编码', ['USP', 'DSP', 'SPI', 'MAINT']],
    ['设备形态', ['一次性', '不锈钢', '虚拟']],
    ['启用', ['是', '否']],
    ['设备类型', activeCategories.map((category) => category.categoryName)],
  ];
  dictionaries.forEach(([title, values], index) => {
    dictionarySheet.getCell(4, index + 1).value = title;
    values.forEach((value, valueIndex) => {
      dictionarySheet.getCell(valueIndex + 5, index + 1).value = value;
    });
  });
  activeModels.forEach((model, index) => {
    dictionarySheet.getCell(index + 5, 9).value = model.categoryName;
    dictionarySheet.getCell(index + 5, 10).value = model.modelName;
  });
  dictionarySheet.getCell('I4').value = '型号所属类型';
  dictionarySheet.getCell('J4').value = '设备型号';
  dictionarySheet.getCell('K4').value = '空白选项';
  dictionarySheet.getCell('K5').value = null;
  dictionarySheet.mergeCells('A1:K1');
  dictionarySheet.getCell('A1').value = '批量导入模板字典';
  dictionarySheet.getCell('A1').font = {
    bold: true,
    size: 16,
    color: { argb: EXCEL_COLORS.headerFont },
  };
  dictionarySheet.getCell('A1').fill = fillFor(EXCEL_COLORS.headerFill);
  dictionarySheet.getCell('A1').alignment = { vertical: 'middle' };
  dictionarySheet.getRow(1).height = 28;
  dictionarySheet.mergeCells('A2:K2');
  dictionarySheet.getCell('A2').value =
    '系统自动生成，只供下拉选择。仅包含基础枚举和人工维护、已启用的可复用设备型号；不会写入任何已有设备实例。请勿在 Excel 中修改。';
  dictionarySheet.getCell('A2').fill = fillFor(EXCEL_COLORS.referenceFill);
  dictionarySheet.getCell('A2').font = { color: { argb: EXCEL_COLORS.referenceFont } };
  dictionarySheet.getCell('A2').alignment = { wrapText: true, vertical: 'middle' };
  dictionarySheet.getRow(2).height = 34;
  const dictionaryHeader = dictionarySheet.getRow(4);
  dictionaryHeader.font = { bold: true, color: { argb: EXCEL_COLORS.headerFont } };
  dictionaryHeader.fill = fillFor(EXCEL_COLORS.headerFill);
  dictionaryHeader.alignment = { vertical: 'middle', wrapText: true };
  dictionaryHeader.height = 34;
  for (let rowIndex = 5; rowIndex <= Math.max(10, activeModels.length + 4); rowIndex += 1) {
    for (let columnIndex = 1; columnIndex <= 10; columnIndex += 1) {
      const cell = dictionarySheet.getCell(rowIndex, columnIndex);
      cell.fill = fillFor(EXCEL_COLORS.referenceFill);
      cell.border = {
        bottom: { style: 'hair', color: { argb: EXCEL_COLORS.border } },
        right: { style: 'hair', color: { argb: EXCEL_COLORS.border } },
      };
    }
  }
  [18, 20, 20, 16, 16, 18, 12, 24, 24, 34, 4].forEach((width, index) => {
    dictionarySheet.getColumn(index + 1).width = width;
  });
  dictionarySheet.getColumn(11).hidden = true;
  dictionarySheet.views = [{
    state: 'frozen',
    ySplit: 4,
    showGridLines: false,
    zoomScale: 90,
  }];
  dictionarySheet.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: Math.max(5, activeModels.length + 4), column: 10 },
  };

  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$A$5:$A$10`, 'NodeTypes');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$B$5:$B$7`, 'RoomSubtypes');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$C$5:$C$6`, 'StationSubtypes');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$D$5:$D$6`, 'ScopeOptions');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$E$5:$E$8`, 'DepartmentCodes');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$F$5:$F$7`, 'SystemTypes');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$G$5:$G$6`, 'EnabledOptions');
  workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$K$5`, 'BlankOption');
  if (activeCategories.length > 0) {
    workbook.definedNames.add(
      `${DICTIONARY_SHEET_NAME}!$H$5:$H$${activeCategories.length + 4}`,
      'EquipmentCategories',
    );
  }
  if (activeModels.length > 0) {
    workbook.definedNames.add(
      `${DICTIONARY_SHEET_NAME}!$I$5:$I$${activeModels.length + 4}`,
      'ModelCategories',
    );
    workbook.definedNames.add(`${DICTIONARY_SHEET_NAME}!$J$5`, 'ModelNameStart');
  }

  nodeSheet.addRow(RESOURCE_NODE_EXCEL_HEADERS);
  applyEntryColumnWidths(nodeSheet);
  configureSheetHeader(nodeSheet);
  HEADER_NOTES.forEach((note, index) => {
    nodeSheet.getCell(1, index + 1).note = note;
  });
  nodeSheet.views = [{
    state: 'frozen',
    xSplit: 3,
    ySplit: 1,
    topLeftCell: 'D2',
    activeCell: 'A2',
    showGridLines: false,
    zoomScale: 85,
  }];

  const lastRow = MAX_RESOURCE_NODE_IMPORT_ROWS + 1;
  styleEntryCells(nodeSheet, 2, lastRow);

  exampleSheet.mergeCells('A1:L1');
  exampleSheet.mergeCells('A2:L2');
  exampleSheet.getCell('A1').value = '资源节点填写示例';
  exampleSheet.getCell('A1').font = {
    bold: true,
    size: 16,
    color: { argb: EXCEL_COLORS.headerFont },
  };
  exampleSheet.getCell('A1').fill = fillFor(EXCEL_COLORS.headerFill);
  exampleSheet.getCell('A2').value =
    '仅供参考，请不要在此 Sheet 录入；正式数据请填写到“节点”Sheet。示例展示父子节点必须使用“节点编码”关联。';
  exampleSheet.getCell('A2').fill = fillFor(EXCEL_COLORS.referenceFill);
  exampleSheet.getCell('A2').font = { color: { argb: EXCEL_COLORS.referenceFont } };
  exampleSheet.getCell('A2').alignment = { wrapText: true, vertical: 'middle' };
  exampleSheet.getRow(1).height = 28;
  exampleSheet.getRow(2).height = 32;
  exampleSheet.addRow([]);
  exampleSheet.addRow(RESOURCE_NODE_EXCEL_HEADERS);
  exampleRows.forEach((row) => exampleSheet.addRow(row));
  applyEntryColumnWidths(exampleSheet);
  configureSheetHeader(exampleSheet, 4);
  styleEntryCells(exampleSheet, 5, 8);
  exampleSheet.views = [{
    state: 'frozen',
    xSplit: 3,
    ySplit: 4,
    topLeftCell: 'D5',
    showGridLines: false,
    zoomScale: 85,
  }];

  addDataValidation(nodeSheet, `A2:A${lastRow}`, {
    type: 'custom',
    allowBlank: false,
    formulae: [
      'IF(A2="",FALSE,AND(LEN(A2)<=64,SUMPRODUCT(--ISNUMBER(SEARCH(MID(A2,ROW(INDIRECT("1:"&LEN(A2))),1),"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_")))=LEN(A2)))',
    ],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: '节点编码无效',
    error: '节点编码必须为 1—64 位，且只能包含英文字母、数字、连字符和下划线。',
    showInputMessage: true,
    promptTitle: '稳定节点编码',
    prompt: '只允许英文字母、数字、连字符和下划线。',
  });
  addDataValidation(nodeSheet, `B2:B${lastRow}`, {
    type: 'textLength',
    operator: 'between',
    allowBlank: false,
    formulae: [1, 120],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: '节点名称无效',
    error: '节点名称长度必须为 1—120 位。',
  });
  addListValidation(nodeSheet, `C2:C${lastRow}`, 'NodeTypes', '选择节点类型。', false);
  addListValidation(
    nodeSheet,
    `D2:D${lastRow}`,
    'INDIRECT(IF($C2="房间","RoomSubtypes",IF($C2="工作站","StationSubtypes","BlankOption")))',
    '房间选择三种房间子类型；工作站选择 CIP 或 SIP；其他节点留空。',
  );
  addDataValidation(nodeSheet, `E2:E${lastRow}`, {
    type: 'textLength',
    operator: 'between',
    allowBlank: true,
    formulae: [1, 64],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: '上级节点编码无效',
    error: '上级节点编码长度必须为 1—64 位。',
  });
  addListValidation(
    nodeSheet,
    `F2:F${lastRow}`,
    'ScopeOptions',
    '选择全局或部门范围。',
    false,
  );
  addListValidation(
    nodeSheet,
    `G2:G${lastRow}`,
    'INDIRECT(IF($F2="部门","DepartmentCodes","BlankOption"))',
    '归属范围为“部门”时选择部门编码；全局范围必须留空。',
  );
  addListValidation(
    nodeSheet,
    `H2:H${lastRow}`,
    'INDIRECT(IF($C2="设备","SystemTypes","BlankOption"))',
    '设备节点选择一次性、不锈钢或虚拟；其他节点必须留空。',
  );
  if (activeCategories.length > 0) {
    addListValidation(
      nodeSheet,
      `I2:I${lastRow}`,
      'INDIRECT(IF($C2="设备","EquipmentCategories","BlankOption"))',
      '物理设备选择已启用类型；设备形态为虚拟时留空。',
    );
  } else {
    addDataValidation(nodeSheet, `I2:I${lastRow}`, {
      type: 'custom',
      allowBlank: true,
      formulae: ['I2=""'],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: '没有可用设备类型',
      error: '请先在系统“设备字典”中维护并启用设备类型。',
    });
  }
  if (activeModels.length > 0) {
    addListValidation(
      nodeSheet,
      `J2:J${lastRow}`,
      'IFERROR(OFFSET(ModelNameStart,MATCH($I2,ModelCategories,0)-1,0,COUNTIF(ModelCategories,$I2),1),BlankOption)',
      '物理设备先选择设备类型，再从对应型号中选择；设备形态为虚拟时留空。',
    );
  } else {
    addDataValidation(nodeSheet, `J2:J${lastRow}`, {
      type: 'custom',
      allowBlank: true,
      formulae: ['J2=""'],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: '没有可用设备型号',
      error: '请先在系统“设备字典”中维护并启用设备型号。',
    });
  }
  addDataValidation(nodeSheet, `K2:K${lastRow}`, {
    type: 'whole',
    operator: 'between',
    allowBlank: true,
    formulae: [0, 999999],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: '排序无效',
    error: '排序只能填写 0—999999 的整数。',
  });
  addListValidation(nodeSheet, `L2:L${lastRow}`, 'EnabledOptions', '留空默认启用。');

  addErrorConditionalFormatting(
    nodeSheet,
    `A2:A${lastRow}`,
    `AND($A2<>"",COUNTIF($A$2:$A$${lastRow},$A2)>1)`,
    1,
  );
  addErrorConditionalFormatting(nodeSheet, `B2:B${lastRow}`, 'AND($A2<>"",$B2="")', 2);
  addErrorConditionalFormatting(nodeSheet, `C2:C${lastRow}`, 'AND($A2<>"",$C2="")', 3);
  addErrorConditionalFormatting(
    nodeSheet,
    `D2:D${lastRow}`,
    'AND($A2<>"",OR(AND(OR($C2="房间",$C2="工作站"),$D2=""),AND(NOT(OR($C2="房间",$C2="工作站")),$D2<>"")))',
    4,
  );
  addErrorConditionalFormatting(
    nodeSheet,
    `E2:E${lastRow}`,
    'AND($A2<>"",OR(AND($C2="厂区",$E2<>""),AND($C2<>"厂区",$E2=""),$A2=$E2))',
    5,
  );
  addErrorConditionalFormatting(nodeSheet, `F2:F${lastRow}`, 'AND($A2<>"",$F2="")', 6);
  addErrorConditionalFormatting(
    nodeSheet,
    `G2:G${lastRow}`,
    'AND($A2<>"",OR(AND($F2="部门",$G2=""),AND($F2="全局",$G2<>"")))',
    7,
  );
  addErrorConditionalFormatting(
    nodeSheet,
    `H2:H${lastRow}`,
    'AND($A2<>"",OR(AND($C2="设备",$H2=""),AND($C2<>"设备",$H2<>"")))',
    8,
  );
  addErrorConditionalFormatting(
    nodeSheet,
    `I2:I${lastRow}`,
    'AND($A2<>"",OR(AND($C2="设备",$H2<>"虚拟",$I2=""),AND($C2="设备",$H2="虚拟",$I2<>""),AND($C2<>"设备",$I2<>"")))',
    9,
  );
  addErrorConditionalFormatting(
    nodeSheet,
    `J2:J${lastRow}`,
    'AND($A2<>"",OR(AND($C2="设备",$H2<>"虚拟",$J2=""),AND($C2="设备",$H2="虚拟",$J2<>""),AND($C2<>"设备",$J2<>""),AND($I2="",$J2<>"")))',
    10,
  );

  return workbook;
};

const triggerDownload = (rows: string[][], fileName: string): void => {
  const ws = XLSX.utils.aoa_to_sheet([RESOURCE_NODE_EXCEL_HEADERS, ...rows]);
  // 适度列宽,便于阅读
  ws['!cols'] = RESOURCE_NODE_EXCEL_HEADERS.map((h) => ({ wch: Math.max(12, h.length * 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);
  XLSX.writeFile(wb, fileName);
};

/** 下载受控导入模板：说明、实时设备字典下拉、字段校验与空白录入页。 */
export const downloadResourceNodeTemplate = async (
  catalog?: ResourceNodeEquipmentCatalog,
): Promise<void> => {
  const workbook = buildResourceNodeImportTemplate(catalog);
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    '资源节点批量导入模板.xlsx',
  );
};

const stamp = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
};

/** 把当前节点树导出为与导入同列格式的 Excel(平铺,父用编码引用) */
export const exportResourceNodesToExcel = (nodes: ResourceNode[]): void => {
  const flat = flattenNodes(nodes);
  const codeById = new Map<number, string>(flat.map((n) => [n.id, n.nodeCode]));

  const rows: string[][] = flat.map((n) => [
    n.nodeCode,
    n.nodeName,
    NODE_CLASS_LABEL[n.nodeClass] ?? n.nodeClass,
    n.nodeSubtype ? SUBTYPE_LABEL[n.nodeSubtype] ?? n.nodeSubtype : '',
    n.parentId ? codeById.get(n.parentId) ?? '' : '',
    SCOPE_LABEL[n.nodeScope] ?? n.nodeScope,
    n.departmentCode ?? '',
    n.equipmentSystemType ? SYSTEM_TYPE_LABEL[n.equipmentSystemType] ?? n.equipmentSystemType : '',
    n.equipmentClass ?? '',
    n.equipmentModel ?? '',
    String(n.sortOrder ?? ''),
    n.isActive ? '是' : '否',
  ]);

  triggerDownload(rows, `资源节点-${stamp()}.xlsx`);
};
